import React from 'react';
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import NewsletterFooterStrip from './NewsletterFooterStrip';
import ClubPaymentDetails from './ClubPaymentDetails';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isGr = i18n.language === 'gr';

  return (
    <footer className="bg-gray-900 text-white">
      {/* Iter200 — newsletter strip sits above the columns so it's the
          first thing a visitor sees when they scroll to the footer.
          Iter380 — the club's legal + bank details sit right next to it so
          members can find the IBAN from any page. */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-2 gap-10 lg:gap-14">
          <div>
            <h3 className="text-xl font-bold mb-4" data-testid="newsletter-footer-title">
              {isGr ? 'Newsletter' : 'Newsletter'}
            </h3>
            <NewsletterFooterStrip />
          </div>
          <div className="lg:border-l lg:border-gray-800 lg:pl-14">
            <h3 className="text-xl font-bold mb-4" data-testid="footer-club-details-title">
              {isGr ? 'Στοιχεία Συλλόγου & Πληρωμών' : 'Club & Payment Details'}
            </h3>
            <ClubPaymentDetails />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img src="/logo.png" alt="E.O.Σ. ΗΛΙΟΥΠΟΛΗΣ" width="48" height="48" loading="lazy" decoding="async" className="h-12 w-12" />
              <span className="text-xl font-bold">ΕΟΣ ΗΛΙΟΥΠΟΛΗΣ</span>
            </div>
            <p className="text-gray-400 leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.aboutUs')}
                </Link>
              </li>
              <li>
                <Link to="/expeditions" className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.expeditions')}
                </Link>
              </li>
              <li>
                <Link to="/classes" className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.classes')}
                </Link>
              </li>
              <li>
                <Link to="/hymettus/climbing" className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.climbing')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">{t('nav.resources')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/usefull/regulations-safety" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.safetyGuides')}
                </Link>
              </li>
              <li>
                <Link to="/usefull/equipment-guide" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.equipmentGuide')}
                </Link>
              </li>
              <li>
                <Link to="/hymettus" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.trailMaps')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <a
                  href="https://maps.app.goo.gl/xokHouqXtUtHF6nv8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                  data-testid="footer-address"
                >
                  {isGr
                    ? 'Υψηλάντου 7, 16341 Ηλιούπολη Αθήνα, Ελλάδα'
                    : '7 Ypsilantou, 16341 Ilioupoli Athens, Greece'}
                </a>
              </li>
              {/* Iter264 — Clubhouse opening hours (Tue / Thu / Fri 20:00-22:00) */}
              <li
                className="flex items-start gap-3 text-gray-400 text-sm"
                data-testid="footer-hours"
              >
                <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>
                  {isGr
                    ? 'Ώρες λειτουργίας: Τρίτη · Πέμπτη · Παρασκευή 20:00–22:00'
                    : 'Open: Tuesday · Thursday · Friday 20:00–22:00'}
                </span>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Phone className="w-5 h-5 flex-shrink-0" />
                <a href="tel:+302109911537" className="hover:text-white transition-colors" data-testid="footer-phone">
                  +30 210 9911537
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Mail className="w-5 h-5 flex-shrink-0" />
                <a href="mailto:info@eosh.gr" className="hover:text-white transition-colors" data-testid="footer-email">
                  info@eosh.gr
                </a>
              </li>
            </ul>

            <div className="flex gap-4 mt-6">
              <a
                href="https://www.facebook.com/AlpineClubIlioupolis"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-3 rounded-full hover:bg-gray-700 transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook-link"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/eos_ilioupolis/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-3 rounded-full hover:bg-gray-700 transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram-link"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/channel/UCQgsNgg0qyFbPRM6uqsbXpg"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800 p-3 rounded-full hover:bg-gray-700 transition-colors"
                aria-label="YouTube"
                data-testid="footer-youtube-link"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;